import Router from "next/router";
import type { Query } from "nextjs-routes";

const SAVED_QUERY_PREFIX = "saved_query_";

/**
 * Saves query parameters for the current pathname to local storage.
 * This allows restoring filters/ordering/pagination when navigating back to a page.
 *
 * @param query - Optional query object to save. If not provided, reads from Router.query
 */
export const saveQuery = (query?: Query): void => {
  if (typeof window === "undefined") return;

  const pathname = Router.pathname;
  const queryToSave = query ?? Router.query;

  try {
    const key = `${SAVED_QUERY_PREFIX}${pathname}`;
    localStorage.setItem(key, JSON.stringify(queryToSave));
  } catch (error) {
    console.error("Failed to save query to local storage:", error);
  }
};

/**
 * Retrieves saved query parameters for the current pathname from local storage.
 * Returns null if no saved query exists or if parsing fails.
 */
export const getQuery = (): Record<string, string | string[]> | null => {
  if (typeof window === "undefined") return null;

  const pathname = Router.pathname;

  try {
    const key = `${SAVED_QUERY_PREFIX}${pathname}`;
    const savedQuery = localStorage.getItem(key);

    if (!savedQuery) return null;

    return JSON.parse(savedQuery);
  } catch (error) {
    console.error("Failed to retrieve query from local storage:", error);
    return null;
  }
};

/**
 * Gets the search parameter from query, with fallback to local storage.
 * Updates local storage if loaded from URL.
 *
 * @param paramKey - The key of query parameter
 * @param query - The URL query object
 * @returns The search term string, or empty string if not found
 */
export function getParamFromQuery(
  paramKey: string,
  query: Query,
): string | undefined {
  if (typeof window === "undefined") return "";

  const param = query[paramKey];
  let paramValue = "";
  let loadedFromSession = false;

  if (!param || param === "") {
    // Load from local storage if current query is empty
    const savedQuery = getQuery();
    const savedQueryParam = savedQuery?.[paramKey];

    if (savedQuery && savedQueryParam) {
      paramValue = Array.isArray(savedQueryParam)
        ? savedQueryParam[0]
        : savedQueryParam;
      loadedFromSession = true;
    }
  } else {
    // Use value from URL
    paramValue = Array.isArray(param) ? param[0] : param;
  }

  // Update local storage if we loaded from URL (not from local)
  // This ensures shared links update the saved state
  if (!loadedFromSession && param) {
    saveQuery(query);
  }

  return paramValue;
}
